import { track } from './amplitude';
import {
  ASSISTANT_RESPONSE_RECEIVED,
  ASSISTANT_RESPONSE_FAILED,
  SCENE_OPEN_FAILED,
  SUGGESTION_CLICKED,
} from './events';
import { currentSceneId } from './trackerContext';
import { errorMessage } from '../utils/errorMessage';
import { isOnline } from '../utils/isOnline';
import {
  assistantPromptAccepted,
  poiClickRequestGenerated,
  suggestionClickAccepted,
  assistantSuggestionSelected,
  publishEventBusFx,
} from '../state/assistantHud';
import {
  sceneOpenRequested,
  publishSceneOpenFx,
} from '../state/sceneOpen';
import { assistantStreamMessageReceived } from '../state/assistantSession';

type RequestSource =
  | 'user_message'
  | 'poi_click'
  | 'suggestion_click'
  | 'scene_open';

type InflightRequest = {
  sentAt: number;
  source: RequestSource;
  sceneId: string | null;
  sightId?: string;
  suggestionType?: string;
  targetSceneId?: string;
};

const inflight = new Map<string, InflightRequest>();

const sourceFromEventType = (
  type: string,
): RequestSource | 'unknown' => {
  if (type === 'user_message') return 'user_message';
  if (type === 'poi_click') return 'poi_click';
  if (type === 'suggestion_click') return 'suggestion_click';
  if (type === 'scene_open') return 'scene_open';
  return 'unknown';
};

export const startAssistantTracking = () => {
  // --- Записываем отправку запросов: requestId → {sentAt, source, context} ---

  assistantPromptAccepted.watch(({ requestId }) => {
    inflight.set(requestId, {
      sentAt: performance.now(),
      source: 'user_message',
      sceneId: currentSceneId(),
    });
  });

  poiClickRequestGenerated.watch(({ id, requestId }) => {
    inflight.set(requestId, {
      sentAt: performance.now(),
      source: 'poi_click',
      sceneId: currentSceneId(),
      sightId: String(id),
    });
  });

  suggestionClickAccepted.watch(({ suggestion, requestId }) => {
    inflight.set(requestId, {
      sentAt: performance.now(),
      source: 'suggestion_click',
      sceneId: currentSceneId(),
      suggestionType: suggestion.type,
      sightId:
        suggestion.type === 'sight' ? String(suggestion.sightId) : undefined,
    });
  });

  sceneOpenRequested.watch(({ sceneId, requestId }) => {
    inflight.set(requestId, {
      sentAt: performance.now(),
      source: 'scene_open',
      sceneId: currentSceneId(),
      targetSceneId: sceneId,
    });
  });

  // --- Успешный ответ ассистента ---

  assistantStreamMessageReceived.watch((message) => {
    const event = message.event;
    if (event.type !== 'assistant_message') return;
    const requestId = event.requestId ?? null;
    if (!requestId) return;
    const tracked = inflight.get(requestId);
    if (!tracked) return;
    inflight.delete(requestId);

    const latencyMs = Math.round(performance.now() - tracked.sentAt);
    const msg = event.message;
    const blocks = 'blocks' in msg ? msg.blocks : undefined;
    const tour = msg.tour;

    track(ASSISTANT_RESPONSE_RECEIVED, {
      source: tracked.source,
      request_id: requestId,
      scene_id: tracked.sceneId,
      target_scene_id: tracked.targetSceneId ?? null,
      sight_id: tracked.sightId ?? null,
      suggestion_type: tracked.suggestionType ?? null,
      latency_ms: latencyMs,
      blocks_count: Array.isArray(blocks) ? blocks.length : 0,
      suggestions_count: msg.suggestions?.length ?? 0,
      actions_count: msg.actions?.length ?? 0,
      has_tour: Array.isArray(tour) && tour.length > 0,
      tour_items_count: Array.isArray(tour) ? tour.length : 0,
    });
  });

  // --- Ошибка POST'а user_message / poi_click / suggestion_click ---

  publishEventBusFx.fail.watch(({ params, error }) => {
    const event = params.event;
    const requestId = 'requestId' in event ? event.requestId ?? null : null;
    const source = sourceFromEventType(event.type);

    const tracked = requestId ? inflight.get(requestId) : null;
    if (requestId && tracked) inflight.delete(requestId);
    const latencyMs = tracked
      ? Math.round(performance.now() - tracked.sentAt)
      : null;

    track(ASSISTANT_RESPONSE_FAILED, {
      source,
      request_id: requestId,
      scene_id: currentSceneId(),
      latency_ms: latencyMs,
      error_message: errorMessage(error),
      online: isOnline(),
    });
  });

  // --- Ошибка POST'а scene_open (отдельное событие — критичный gate) ---

  publishSceneOpenFx.fail.watch(({ params, error }) => {
    const event = params.event;
    const requestId = 'requestId' in event ? event.requestId ?? null : null;
    const targetSceneId =
      event.type === 'scene_open' ? event.sceneId : null;

    const tracked = requestId ? inflight.get(requestId) : null;
    if (requestId && tracked) inflight.delete(requestId);
    const latencyMs = tracked
      ? Math.round(performance.now() - tracked.sentAt)
      : null;

    track(SCENE_OPEN_FAILED, {
      target_scene_id: targetSceneId,
      scene_id: currentSceneId(),
      request_id: requestId,
      latency_ms: latencyMs,
      error_message: errorMessage(error),
      online: isOnline(),
    });
  });

  // --- Клик по suggestion (пользовательский intent) ---

  assistantSuggestionSelected.watch((suggestion) => {
    const sightId =
      suggestion.type === 'sight' ? String(suggestion.sightId) : null;
    const label = 'label' in suggestion ? suggestion.label ?? null : null;

    track(SUGGESTION_CLICKED, {
      suggestion_type: suggestion.type,
      sight_id: sightId,
      label,
      scene_id: currentSceneId(),
    });
  });
};
